/**
 * Sentry is lazy-loaded so the @sentry/react SDK (~100KB gzip with replay +
 * tracing) is not in the entry chunk. Public landing and SEO pages — where
 * users haven't even signed in — never pay the cost.
 *
 * `initSentry()` returns a promise that resolves once the SDK has been
 * downloaded and configured. Callers can await or fire-and-forget.
 *
 * The DSN is read from VITE_SENTRY_DSN. Missing DSN or non-production
 * builds → no-op.
 *
 * Sample rates are intentionally low until we have traffic to learn from:
 *   tracesSampleRate          0.01 — 1% of page loads emit a tracing transaction
 *   replaysSessionSampleRate  0.005 — 0.5% of sessions are recorded
 *   replaysOnErrorSampleRate  0.25 — 25% of errors capture replay
 * Tune up later once we have a baseline.
 */
/**
 * Does this string look like a DSN the SDK can parse?
 *
 * Deliberately permissive about everything except the parts Sentry needs:
 * a URL with a username (the public key) and a non-empty path (the project id).
 * The goal is to catch an empty placeholder, a truncated paste or a stray
 * quote — not to re-implement the SDK's parser.
 */
function isParsableDsn(dsn: string): boolean {
  try {
    const url = new URL(dsn);
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      url.username !== "" &&
      url.pathname.replace(/\//g, "") !== ""
    );
  } catch {
    return false;
  }
}

export async function initSentry(): Promise<void> {
  if (!import.meta.env.PROD) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.info("[sentry] VITE_SENTRY_DSN not set — error tracking disabled");
    return;
  }

  // A MALFORMED DSN MUST NOT COST EVERY PAGE AN ERROR.
  //
  // The empty case was handled and the wrong case was not. Sentry.init throws
  // "Invalid Sentry Dsn" on a value it cannot parse, and it does it on every
  // authenticated page load — which is exactly what the deployed stage build
  // does today. Two costs: the console is full of an error nobody caused, and
  // the E2E suite fails 26 tests against deployed stage because gotoClean()
  // treats any console error as a failure. Correctly: a page that logs errors
  // on load is a page with something wrong.
  //
  // Parsed rather than regexed, because the shape that matters is the one the
  // SDK will parse: protocol://publicKey@host/projectId.
  if (!isParsableDsn(dsn)) {
    console.info(
      "[sentry] VITE_SENTRY_DSN is set but not a valid DSN — error tracking disabled",
    );
    return;
  }

  // Lazy import — the chunk doesn't load until this function is called,
  // and (per main.tsx) we only call it for authenticated routes.
  const Sentry = await import("@sentry/react");

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.01,
    replaysSessionSampleRate: 0.005,
    replaysOnErrorSampleRate: 0.25,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      /^Non-Error promise rejection captured/,
    ],
  });
}

/**
 * Lazily reports an exception via Sentry. If Sentry hasn't been initialized
 * yet (public visitor, dev, or DSN missing) this is a no-op — call
 * `initSentry()` first (the ErrorBoundary does this on-catch so even public
 * `/auth` crashes get reported without pre-loading the SDK for every visitor).
 *
 * `context` is attached as `extra` on the event — carry the hard-to-reconstruct
 * signals (active language, whether the browser was auto-translating the DOM,
 * user agent, component stack).
 */
export async function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  if (!import.meta.env.PROD) return;
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  const Sentry = await import("@sentry/react");
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * Attach context tags to every event Sentry sends from this point on.
 * Use from React effects when the active language or business changes
 * so debugging a prod error includes "this happened to an `es` user in
 * business <id>" instead of just the stack trace.
 *
 * No-op for public surfaces / non-prod / missing DSN.
 */
export async function setSentryTags(tags: {
  locale?: string;
  businessId?: string | null;
  role?: string | null;
}): Promise<void> {
  if (!import.meta.env.PROD) return;
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  const Sentry = await import("@sentry/react");
  if (tags.locale) Sentry.setTag("locale", tags.locale);
  if (tags.businessId) Sentry.setTag("business_id", tags.businessId);
  if (tags.role) Sentry.setTag("role", tags.role);
}
